import {
  IMPORT_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
  PREVIEW_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
  RETRIEVE_SYNC_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_DATA_API,
  UPDATE_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKET_API,
} from "../api/api_data";
import { makeEmployeeDashboardApiRequest } from "../api/api_helper";
import { sanitizeWeighbridgePreviewCsvFile } from "../utils/weighbridgePreviewCsv";

const LOCAL_AGENT_BASE_URL = process.env.REACT_APP_WEIGHBRIDGE_AGENT_URL || "http://localhost:4002";
const LAST_WEIGHBRIDGE_SYNC_KEY = "weighbridge_last_sync_meta";

const readCurrentCred = () => {
  try
  {
    const storedCred = localStorage.getItem("log_cred");
    return storedCred ? JSON.parse(storedCred) : null;
  } catch (error)
  {
    console.error("Failed to parse log_cred while resolving weighbridge sync context.", error);
    return null;
  }
};

const getCurrentBranchLocationId = () => {
  const currentCred = readCurrentCred();

  if (!currentCred)
  {
    return "";
  }

  const branchCandidates = [
    currentCred?.branch_location_id,
    currentCred?.assigned_branch_location_id,
    currentCred?.employee_branch_location_id,
    currentCred?.default_branch_location_id,
    currentCred?.branch_id,
    currentCred?.property_ref_code,
  ];

  const resolvedBranch = branchCandidates.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return resolvedBranch ? String(resolvedBranch) : "";
};

const resolveIntegrationScope = (integrations = [], branchLocationId = "") => {
  if (!Array.isArray(integrations) || integrations.length === 0)
  {
    return [];
  }

  if (branchLocationId)
  {
    return integrations.filter((integration) => String(integration?.branch_location_id) === String(branchLocationId));
  }

  if (integrations.length === 1)
  {
    return integrations;
  }

  return [];
};

const normalizeIntegrationRows = (rows = []) =>
  rows.map((row) => ({
    ...(row?.integration || {}),
    branch_location: row?.branch_location || {},
    branch_location_id: row?.integration?.branch_location_id || row?.branch_location?.branch_location_id || "",
    system: row?.integration?.system || row?.integration?.integration_name || "",
  }));

const getIntegrationIdentifier = (integration = {}) => {
  const identifierCandidates = [
    integration?.integration_id,
    integration?.branch_wbdge_integration_state_id,
    integration?.branch_integration_id,
    integration?.integration_state_id,
    integration?.id,
  ];

  return (
    identifierCandidates.find((value) => value !== null && value !== undefined && value !== "") || ""
  );
};

const buildWeighbridgeIntegrationFormData = ({
  integration = {},
  csvFile,
  fallbackBranchLocationId = "",
} = {}) => {
  const formData = new FormData();
  const integrationIdentifier = getIntegrationIdentifier(integration);
  const resolvedBranchLocationId =
    integration?.branch_location_id || integration?.branch_location?.branch_location_id || fallbackBranchLocationId || "";

  formData.append("integration_name", integration?.integration_name || "");

  if (integration?.system) {
    formData.append("system", integration.system);
  }

  if (resolvedBranchLocationId !== "") {
    formData.append("branch_location_id", String(resolvedBranchLocationId));
  }

  if (integrationIdentifier !== "") {
    formData.append("integration_id", String(integrationIdentifier));
    formData.append("branch_wbdge_integration_state_id", String(integrationIdentifier));
  }

  if (csvFile) {
    formData.append("document", csvFile, csvFile?.name || "weighbridge_tickets.csv");
  }

  return formData;
};

const resolveMatchedIntegration = (agentInfo = {}, integrations = []) => {
  if (!Array.isArray(integrations) || integrations.length === 0)
  {
    return null;
  }

  const agentReferenceCandidates = [
    agentInfo?.integration_reference_number,
    agentInfo?.integration_name,
    agentInfo?.reference_number,
    agentInfo?.agent_reference_number,
  ].filter(Boolean);

  if (agentReferenceCandidates.length === 0)
  {
    return null;
  }

  return (
    integrations.find((integration) =>
      agentReferenceCandidates.some((candidate) => candidate === integration?.integration_name),
    ) || null
  );
};

const extractPreviewPayload = (responseData = {}) => {
  if (responseData?.data && typeof responseData.data === "object")
  {
    return responseData.data;
  }

  return responseData;
};

const getPreviewTickets = (data) => {
  if (Array.isArray(data))
  {
    return data;
  }

  if (Array.isArray(data?.tickets))
  {
    return data.tickets;
  }

  return [];
};

const getValidTicketCount = (previewData = {}) => {
  if (typeof previewData?.total_valid_tickets_count === "number")
  {
    return previewData.total_valid_tickets_count;
  }

  return getPreviewTickets(previewData?.total_valid_tickets).length;
};

const getImportedTicketCount = (importData = {}, fallbackCount = 0) => {
  const successfulCreations = getPreviewTickets(importData?.success_creation_tickets).length;

  if (successfulCreations > 0)
  {
    return successfulCreations;
  }

  if (typeof importData?.total_tickets === "number")
  {
    return importData.total_tickets;
  }

  return fallbackCount;
};

const persistLastWeighbridgeSyncMeta = (meta = {}) => {
  try {
    localStorage.setItem(
      LAST_WEIGHBRIDGE_SYNC_KEY,
      JSON.stringify({
        syncedAt: new Date().toISOString(),
        ...meta,
      }),
    );
  } catch (error) {
    console.error("Could not persist weighbridge last sync metadata.", error);
  }
};

export const getLastWeighbridgeSyncMeta = () => {
  try {
    const storedValue = localStorage.getItem(LAST_WEIGHBRIDGE_SYNC_KEY);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (error) {
    console.error("Could not read weighbridge last sync metadata.", error);
    return null;
  }
};

const createCsvFileFromAgentResponse = async (response, integrationName) => {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json"))
  {
    const data = await response.json();
    const csvContent = data?.csv_content || data?.csv || "";

    if (!csvContent)
    {
      throw new Error("The local agent returned no CSV content.");
    }

    const csvFile = new File(
      [csvContent],
      data?.file_name || `${integrationName || "weighbridge_tickets"}.csv`,
      { type: "text/csv" },
    );

    return sanitizeWeighbridgePreviewCsvFile(csvFile);
  }

  const blob = await response.blob();
  const csvFile = new File([blob], `${integrationName || "weighbridge_tickets"}.csv`, {
    type: "text/csv",
  });
  return sanitizeWeighbridgePreviewCsvFile(csvFile);
};

const getTicketExternalNumber = (ticket = {}) => {
  const externalTicketCandidates = [
    ticket?.external_weigh_bridge_ticket_number,
    ticket?.ticket?.external_weigh_bridge_ticket_number,
    ticket?.raw?.ticket?.external_weigh_bridge_ticket_number,
  ];

  const resolvedExternalNumber = externalTicketCandidates.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return resolvedExternalNumber ? String(resolvedExternalNumber).trim() : "";
};

const getTicketIntegrationName = (ticket = {}) => {
  const integrationCandidates = [
    ticket?.source_system,
    ticket?.integration_name,
    ticket?.ticket?.source_system,
    ticket?.raw?.ticket?.source_system,
  ];

  const resolvedIntegration = integrationCandidates.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return resolvedIntegration ? String(resolvedIntegration).trim() : "";
};

const getTicketBranchLocationId = (ticket = {}) => {
  const branchCandidates = [
    ticket?.branch_location_id,
    ticket?.ticket?.branch_location_id,
    ticket?.raw?.ticket?.branch_location_id,
  ];

  const resolvedBranch = branchCandidates.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return resolvedBranch ? String(resolvedBranch).trim() : "";
};

const extractAgentTicketPayload = (agentResponse = {}, requestedExternalTicketNumber = "") => {
  const rows = [];

  const appendRow = (row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      rows.push(row);
    }
  };

  const appendRows = (list) => {
    if (Array.isArray(list)) {
      list.forEach(appendRow);
    }
  };

  if (Array.isArray(agentResponse)) {
    appendRows(agentResponse);
  }

  appendRow(agentResponse?.ticket);
  appendRows(agentResponse?.tickets);
  appendRow(agentResponse?.data);
  appendRow(agentResponse?.payload);
  appendRows(agentResponse?.data?.tickets);
  appendRows(agentResponse?.data?.rows);
  appendRows(agentResponse?.rows);
  appendRow(agentResponse);

  const normalizedExternalNumber = String(requestedExternalTicketNumber || "").trim();
  const matchedRow = normalizedExternalNumber
    ? rows.find((row) => getTicketExternalNumber(row) === normalizedExternalNumber)
    : rows[0];

  const candidatePayload =
    matchedRow?.raw_payload ||
    matchedRow?.ticket?.raw_payload ||
    matchedRow?.payload ||
    matchedRow?.ticket ||
    null;

  if (!candidatePayload || Array.isArray(candidatePayload) || typeof candidatePayload !== "object") {
    return null;
  }

  return candidatePayload;
};

export const getWeighbridgeSyncFallbackMessage = (error) => {
  const rawMessage = String(error?.message || "").trim();
  const normalizedMessage = rawMessage.toLowerCase();

  if (!rawMessage || normalizedMessage === "failed to fetch")
  {
    return "Could not sync from the local weighbridge agent right now. Showing the latest tickets already available on the server.";
  }

  return `${rawMessage} Showing the latest tickets already available on the server.`;
};

export const NO_NEW_VALID_WEIGHBRIDGE_TICKETS_MESSAGE =
  "No new valid weighbridge tickets were available to sync.";

export const updateBranchWeighbridgeIntegrationTicket = async ({
  integrationName,
  externalWeighBridgeTicketNumber,
  rawPayload,
}) => {
  return makeEmployeeDashboardApiRequest(
    "get",
    UPDATE_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKET_API,
    {
      integration_name: integrationName,
      external_weigh_bridge_ticket_number: externalWeighBridgeTicketNumber,
      raw_payload: rawPayload,
    },
  );
};

export const updateWeighbridgeTicketFromAgent = async ({
  ticket,
  onStatus,
} = {}) => {
  const externalWeighBridgeTicketNumber = getTicketExternalNumber(ticket);

  if (!externalWeighBridgeTicketNumber) {
    throw new Error("This ticket does not have an external ticket number to update.");
  }

  onStatus?.("Detecting local weighbridge agent...");

  const healthResponse = await fetch(`${LOCAL_AGENT_BASE_URL}/health`, { method: "GET" });

  if (!healthResponse.ok) {
    throw new Error("The local weighbridge agent did not respond successfully.");
  }

  const agentData = await healthResponse.json();

  onStatus?.("Resolving saved branch integration...");

  const integrationResponse = await makeEmployeeDashboardApiRequest(
    "get",
    RETRIEVE_SYNC_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_DATA_API,
  );

  const integrationRows = Array.isArray(integrationResponse?.data?.data) ? integrationResponse.data.data : [];
  const normalizedRows = normalizeIntegrationRows(integrationRows);
  const ticketBranchLocationId = getTicketBranchLocationId(ticket);
  const currentBranchLocationId = getCurrentBranchLocationId();
  const scopedRows = resolveIntegrationScope(
    normalizedRows,
    ticketBranchLocationId || currentBranchLocationId,
  );
  const candidateRows = scopedRows.length > 0 ? scopedRows : normalizedRows;
  const preferredIntegrationName = getTicketIntegrationName(ticket);

  let matchedIntegration = null;

  if (preferredIntegrationName) {
    matchedIntegration =
      candidateRows.find((integration) => integration?.integration_name === preferredIntegrationName) ||
      normalizedRows.find((integration) => integration?.integration_name === preferredIntegrationName) ||
      null;
  }

  if (!matchedIntegration) {
    matchedIntegration = resolveMatchedIntegration(agentData, candidateRows);
  }

  if (!matchedIntegration?.integration_name) {
    throw new Error("No saved branch integration matched this weighbridge ticket.");
  }

  onStatus?.(
    `Loading external ticket ${externalWeighBridgeTicketNumber} from ${matchedIntegration.integration_name}...`,
  );

  const exportResponse = await fetch(`${LOCAL_AGENT_BASE_URL}/export-csv`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integration_reference_number: matchedIntegration.integration_name,
      external_weigh_bridge_ticket_number: externalWeighBridgeTicketNumber,
      external_ticket_number: externalWeighBridgeTicketNumber,
      ticket_reference_number: externalWeighBridgeTicketNumber,
    }),
  });

  if (!exportResponse.ok) {
    throw new Error("The local weighbridge agent could not export this ticket.");
  }

  const contentType = exportResponse.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const agentPayload = await exportResponse.json();
    const latestRawPayload = extractAgentTicketPayload(
      agentPayload,
      externalWeighBridgeTicketNumber,
    );

    if (latestRawPayload) {
      onStatus?.("Applying ticket updates...");

      const response = await updateBranchWeighbridgeIntegrationTicket({
        integrationName: matchedIntegration.integration_name,
        externalWeighBridgeTicketNumber,
        rawPayload: latestRawPayload,
      });

      return {
        matchedIntegration,
        strategy: "update",
        message: response?.data?.message || "Weighbridge ticket updated successfully.",
      };
    }
  }

  onStatus?.("Falling back to the ticket sync flow...");

  const syncResult = await syncWeighbridgeTicketsFromAgent({
    externalWeighBridgeTicketNumber,
    onStatus,
  });

  return {
    matchedIntegration,
    ...syncResult,
    strategy: "sync_fallback",
    message: syncResult?.message || "Weighbridge ticket refreshed successfully.",
  };
};

export const syncWeighbridgeTicketsFromAgent = async ({
  externalWeighBridgeTicketNumber = "",
  onStatus,
} = {}) => {
  const requestedExternalTicketNumber = String(externalWeighBridgeTicketNumber || "").trim();

  onStatus?.("Detecting local weighbridge agent...");

  const healthResponse = await fetch(`${LOCAL_AGENT_BASE_URL}/health`, { method: "GET" });

  if (!healthResponse.ok)
  {
    throw new Error("The local weighbridge agent did not respond successfully.");
  }

  const agentData = await healthResponse.json();

  onStatus?.("Resolving saved branch integration...");

  const integrationResponse = await makeEmployeeDashboardApiRequest(
    "get",
    RETRIEVE_SYNC_BRANCH_LOCATION_WEIGH_BRIDGE_INTEGRATION_DATA_API,
  );

  const integrationRows = Array.isArray(integrationResponse?.data?.data) ? integrationResponse.data.data : [];
  const normalizedRows = normalizeIntegrationRows(integrationRows);
  const currentBranchLocationId = getCurrentBranchLocationId();
  const branchScopedRows = resolveIntegrationScope(normalizedRows, currentBranchLocationId);

  if (branchScopedRows.length === 0)
  {
    if (currentBranchLocationId)
    {
      throw new Error(`No saved weighbridge integration is configured for branch ${currentBranchLocationId}.`);
    }

    throw new Error("Could not resolve the current branch for automatic weighbridge sync.");
  }

  const matchedIntegration = resolveMatchedIntegration(agentData, branchScopedRows);

  if (!matchedIntegration?.integration_name)
  {
    throw new Error("No saved branch integration matched the detected local weighbridge agent for this branch.");
  }

  onStatus?.(
    requestedExternalTicketNumber
      ? `Looking up external ticket ${requestedExternalTicketNumber} from ${matchedIntegration.integration_name}...`
      : `Loading CSV from ${matchedIntegration.integration_name}...`,
  );

  const exportResponse = await fetch(`${LOCAL_AGENT_BASE_URL}/export-csv`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integration_reference_number: matchedIntegration.integration_name,
      external_weigh_bridge_ticket_number: requestedExternalTicketNumber || undefined,
      external_ticket_number: requestedExternalTicketNumber || undefined,
      ticket_reference_number: requestedExternalTicketNumber || undefined,
    }),
  });

  if (!exportResponse.ok)
  {
    throw new Error("The local weighbridge agent could not export CSV data.");
  }

  const csvFile = await createCsvFileFromAgentResponse(exportResponse, matchedIntegration.integration_name);

  onStatus?.("Previewing weighbridge tickets...");

  const previewFormData = buildWeighbridgeIntegrationFormData({
    integration: matchedIntegration,
    csvFile,
    fallbackBranchLocationId: currentBranchLocationId,
  });

  const previewResponse = await makeEmployeeDashboardApiRequest(
    "post",
    PREVIEW_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
    previewFormData,
  );

  const previewData = extractPreviewPayload(previewResponse?.data);
  const validTicketCount = getValidTicketCount(previewData);

  if (validTicketCount <= 0)
  {
    persistLastWeighbridgeSyncMeta({
      integrationName: matchedIntegration.integration_name,
      importedCount: 0,
      message: NO_NEW_VALID_WEIGHBRIDGE_TICKETS_MESSAGE,
    });

    return {
      matchedIntegration,
      previewData,
      importedCount: 0,
      message: "No new valid weighbridge tickets were available to sync.",
    };
  }

  onStatus?.(`Importing ${validTicketCount} valid weighbridge tickets...`);

  const importFormData = buildWeighbridgeIntegrationFormData({
    integration: matchedIntegration,
    csvFile,
    fallbackBranchLocationId: currentBranchLocationId,
  });

  const importResponse = await makeEmployeeDashboardApiRequest(
    "post",
    IMPORT_BRANCH_WEIGH_BRIDGE_INTEGRATION_TICKETS_SYNC_API,
    importFormData,
  );

  const importData = extractPreviewPayload(importResponse?.data);
  const importedCount = getImportedTicketCount(importData, validTicketCount);
  const successMessage = importResponse?.data?.message || "Weighbridge tickets synced successfully.";

  persistLastWeighbridgeSyncMeta({
    integrationName: matchedIntegration.integration_name,
    importedCount,
    message: successMessage,
  });

  return {
    matchedIntegration,
    previewData,
    importData,
    importedCount,
    message: successMessage,
  };
};
